import { cn } from '@/lib/cn'

export type ProgramView = 'programs' | 'unassigned' | 'merged' | 'slate' | 'other'
export type ProgramLevel = 'undergraduate' | 'graduate'

const VIEWS: Array<{ value: ProgramView; label: string }> = [
  { value: 'programs', label: 'Programs' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'merged', label: 'Merged' },
  { value: 'slate', label: 'Slate programs' },
  { value: 'other', label: 'Other programs' },
]

const LEVELS: Array<{ value: ProgramLevel; label: string }> = [
  { value: 'undergraduate', label: 'Undergraduate' },
  { value: 'graduate', label: 'Graduate' },
]

/**
 * The views a level has. WB programs and slate programs are undergraduate
 * (PC-25, ADR-0022), so Graduate has no Unassigned or Slate programs view.
 */
export const LEVEL_VIEWS: Record<ProgramLevel, ProgramView[]> = {
  undergraduate: ['programs', 'unassigned', 'merged', 'slate', 'other'],
  graduate: ['programs', 'merged', 'other'],
}

/**
 * Above the status tabs, not beside them: crawled programs under no WB program
 * (PC-33), merged duplicates, slate programs and programs without a page of
 * their own are different rows, not a filter over the same ones. Styled as a segmented
 * control so the two rows of tabs do not read as one.
 */
export function ViewTabs({
  value,
  views,
  counts,
  onChange,
}: {
  value: ProgramView
  views: ProgramView[]
  counts: Record<ProgramView, number>
  onChange: (next: ProgramView) => void
}) {
  return (
    <Segmented
      label="Program views"
      options={VIEWS.filter((v) => views.includes(v.value))}
      value={value}
      counts={counts}
      onChange={onChange}
    />
  )
}

/** Undergraduate or Graduate (PC-25), above the views: every view below is one level's rows. */
export function LevelTabs({
  value,
  counts,
  onChange,
}: {
  value: ProgramLevel
  counts: Record<ProgramLevel, number>
  onChange: (next: ProgramLevel) => void
}) {
  return <Segmented label="Program level" options={LEVELS} value={value} counts={counts} onChange={onChange} />
}

function Segmented<T extends string>({
  label,
  options,
  value,
  counts,
  onChange,
}: {
  label: string
  options: Array<{ value: T; label: string }>
  value: T
  counts: Record<T, number>
  onChange: (next: T) => void
}) {
  return (
    <div role="tablist" aria-label={label} className="inline-flex gap-1 rounded-md bg-surface-sunk p-0.5">
      {options.map((option) => {
        const active = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex items-center gap-1.5 rounded px-2.5 py-1 font-head text-[12px] font-semibold transition-colors',
              active ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
            )}
          >
            {option.label}
            <span className="tabular font-normal text-ink-faint">{counts[option.value]}</span>
          </button>
        )
      })}
    </div>
  )
}
